import { create } from 'zustand'
import { scoutService } from '../services/scoutService'
import { scoutAccountService } from '../services/scoutAccountService'
import {
  ScoutBrand,
  ScoutBrandPost,
  ScoutBrandSummary,
  ScoutBrandPostsSummary,
  ScoutPagination,
  ScoutAccount,
  ScoutAccountLimits
} from '../types/scout'

interface ScoutState {
  // Brand list state
  brands: ScoutBrand[]
  summary: ScoutBrandSummary | null
  pagination: ScoutPagination | null
  loading: boolean
  error: string | null

  // Brand detail state
  selectedBrand: ScoutBrand | null
  brandPosts: ScoutBrandPost[]
  postsPagination: ScoutPagination | null
  postsSummary: ScoutBrandPostsSummary | null
  detailLoading: boolean
  postsLoading: boolean

  // Account state
  accounts: ScoutAccount[]
  accountLimits: ScoutAccountLimits | null
  accountsLoading: boolean
  accountsError: string | null

  // Categories state
  brandCategories: string[]
  categoriesLoading: boolean

  // Brand actions
  loadBrands: (
    customerId: string,
    page?: number,
    limit?: number,
    sortBy?: string,
    order?: string,
    search?: string,
    accountIds?: string[],
    period?: string,
    customStartDate?: string,
    customEndDate?: string,
    categories?: string[]
  ) => Promise<void>
  loadBrandCategories: (customerId: string) => Promise<void>
  loadBrandDetail: (brandId: string, customerId: string) => Promise<void>
  loadBrandPosts: (
    brandId: string,
    customerId: string,
    page?: number,
    limit?: number,
    accountIds?: string[],
    period?: string,
    customStartDate?: string,
    customEndDate?: string,
    detectionTypes?: string[],
    placementTiers?: string[],
    contentCategories?: string[]
  ) => Promise<void>

  // Account actions
  loadAccounts: (customerId: string, page?: number, limit?: number) => Promise<void>
  createAccount: (customerId: string, username: string, platformId?: number) => Promise<ScoutAccount>
  updateAccount: (
    id: string,
    customerId: string,
    updates: { is_active?: boolean; display_name?: string }
  ) => Promise<ScoutAccount>
  deleteAccount: (id: string, customerId: string) => Promise<void>

  reset: () => void
}

const initialState = {
  brands: [],
  summary: null,
  pagination: null,
  loading: false,
  error: null,
  selectedBrand: null,
  brandPosts: [],
  postsPagination: null,
  postsSummary: null,
  detailLoading: false,
  postsLoading: false,
  accounts: [],
  accountLimits: null,
  accountsLoading: false,
  accountsError: null,
  brandCategories: [],
  categoriesLoading: false
}

const useScoutStore = create<ScoutState>((set) => ({
  ...initialState,

  loadBrands: async (customerId, page, limit, sortBy, order, search, accountIds, period, customStartDate, customEndDate, categories) => {
    set({ loading: true, error: null })
    try {
      const response = await scoutService.getBrands(
        customerId,
        page,
        limit,
        sortBy,
        order,
        search,
        accountIds,
        period,
        customStartDate,
        customEndDate,
        categories
      )
      set({
        brands: response.data,
        summary: response.summary,
        pagination: response.pagination,
        loading: false
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load brands',
        loading: false
      })
    }
  },

  loadBrandDetail: async (brandId, customerId) => {
    set({ detailLoading: true })
    try {
      const response = await scoutService.getBrandById(brandId, customerId)
      set({ selectedBrand: response.data, detailLoading: false })
    } catch (error) {
      set({ detailLoading: false })
      throw error
    }
  },

  loadBrandPosts: async (brandId, customerId, page, limit, accountIds, period, customStartDate, customEndDate, detectionTypes, placementTiers, contentCategories) => {
    set({ postsLoading: true })
    try {
      const response = await scoutService.getBrandPosts(
        brandId,
        customerId,
        page,
        limit,
        accountIds,
        period,
        customStartDate,
        customEndDate,
        detectionTypes,
        placementTiers,
        contentCategories
      )
      set({
        brandPosts: response.data,
        postsPagination: response.pagination,
        postsSummary: response.summary ?? null,
        postsLoading: false
      })
    } catch (error) {
      set({ postsLoading: false })
      throw error
    }
  },

  loadAccounts: async (customerId, page, limit) => {
    set({ accountsLoading: true, accountsError: null })
    try {
      // Brand Scout shows competitor accounts only (owned/partner live in Content Performance).
      const response = await scoutAccountService.getAccounts(customerId, page, limit, false)
      set({
        accounts: response.data,
        accountLimits: response.limits ?? null,
        accountsLoading: false
      })
    } catch (error) {
      set({
        accountsError: error instanceof Error ? error.message : 'Failed to load accounts',
        accountsLoading: false
      })
    }
  },

  createAccount: async (customerId, username, platformId = 1) => {
    const response = await scoutAccountService.createAccount(customerId, username, platformId)
    return response.data
  },

  updateAccount: async (id, customerId, updates) => {
    const response = await scoutAccountService.updateAccount(id, customerId, updates)
    return response.data
  },

  deleteAccount: async (id, customerId) => {
    await scoutAccountService.deleteAccount(id, customerId)
    set((state) => ({
      accounts: state.accounts.filter((a) => a.id !== id)
    }))
  },

  loadBrandCategories: async (customerId) => {
    set({ categoriesLoading: true })
    try {
      const response = await scoutService.getCategories(customerId)
      set({ brandCategories: response.data, categoriesLoading: false })
    } catch {
      set({ categoriesLoading: false })
    }
  },

  reset: () => set(initialState)
}))

export default useScoutStore
